import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Box } from '@bonusly/design-system';

import type { BizyAssistantMessageNode } from '../../../schemas/bizy-conversations';
import type { BizyUiBlock } from '../../../schemas/bizy-ui-blocks';
import type { ApprovalDecision, CardNode } from '../hooks/use-bizy-global-chat';
import { UiBlockRenderer } from '../ui-blocks/ui-block-renderer';
import { BizyCardRenderer } from './bizy-card-renderer';
import { BizyChart } from './bizy-chart';
import { BizyFollowUpSuggestions } from './bizy-follow-up-suggestions';
import { BizyMatrix } from './bizy-matrix';
import { BizyScorecard } from './bizy-scorecard';
import { BizySourceDetails } from './bizy-source-details';
import { BizyStreamingMessage } from './bizy-streaming-message';
import { BizyTable } from './bizy-table';

interface BizyDocumentRendererProps {
	fontSize?: 'sm' | 'md';
	/** Ordered sequence of typed nodes — markdown content interleaved with
	 *  card nodes. Walked top-to-bottom; markdown nodes render via
	 *  Streamdown, card nodes via BizyCardRenderer. */
	nodes: BizyAssistantMessageNode[];
	/** Whether the streaming turn is complete. Forwarded to the markdown
	 *  renderer so the in-progress styling (italic, pulsing cursor) clears
	 *  when the document is finalized. */
	isComplete?: boolean;
	/** Stable key for the message — used by card components to record per-card
	 *  user choices (approve / draft confirm). */
	messageKey?: string;
	/** Called when content updates (for auto-scroll). */
	onContentUpdate?: () => void;
	/** Tool progress labels shown while tools are running. Forwarded only to
	 *  the active (last) markdown node — the one currently receiving tokens. */
	toolProgress?: string[];
	/** Rotating thinking phrase. Forwarded only to the active (last) markdown
	 *  node — the one currently receiving tokens. */
	thinkingPhrase?: string;
	/** User choices keyed by the position of the interactive (card-typed)
	 *  node within the document. Lets the renderer show post-click state
	 *  (e.g., "Sent ✓") after a reload. */
	nodeChoices?: Record<number, ApprovalDecision>;
	/** Per-card callbacks — same contract as BizyCardRenderer. */
	onConfirm?: (cardIndex: number, confirmMessage: string) => void;
	onChoiceUpdate?: (messageKey: string, cardIndex: number, choice: ApprovalDecision) => void;
	onSuggestedPageClick?: (path: string) => void;
	/** Send a follow-up suggestion verbatim as the next message (one-tap). */
	onSelectFollowUp?: (suggestion: string) => void;
	/** Drill into an evidence element (scorecard metric, chart bar, heatmap
	 *  cell, table row) — sends the renderer-built scoped follow-up verbatim. */
	onDrill?: (message: string) => void;
	/** Disable follow-up rows and scorecard drill-in while a previous turn is
	 *  still processing. */
	followUpsDisabled?: boolean;
	/** Disables customer-affecting card actions while keeping links and text selectable. */
	isReadOnly?: boolean;
}

/**
 * Renders a typed AssistantMessage document.
 *
 * Walks the node array in order and dispatches per `type`:
 * - `markdown` → BizyStreamingMessage (Streamdown markdown renderer).
 * - `approval_card` / `content_card` / `draft_card` → BizyCardRenderer
 *   (per-card components for each card type).
 * - `recognition_draft_card` / `comment_draft_card` /
 *   `recognition_view_card` / `recognition_appreciation_card` → typed UiBlockRenderer.
 *
 * `cardIndex` for card callbacks tracks the cumulative position of card
 * nodes across the document — a card emitted after multiple markdown
 * sections gets a unique index for choice tracking.
 */
// Naive singular→plural for the expand affordance's entity noun (an axis or
// column label). Handles the common consonant+y → -ies; the agent tolerates
// the rest. Empty when there's no usable label.
function pluralize(noun?: string): string {
	const n = noun?.trim().toLowerCase();
	if (!n) return '';
	if (/s$/.test(n)) return n;
	if (/[^aeiou]y$/.test(n)) return n.replace(/y$/, 'ies');
	return `${n}s`;
}

export function BizyDocumentRenderer({
	fontSize,
	nodes,
	isComplete = false,
	messageKey,
	onContentUpdate,
	toolProgress,
	thinkingPhrase,
	nodeChoices,
	onConfirm,
	onChoiceUpdate,
	onSuggestedPageClick,
	onSelectFollowUp,
	onDrill,
	followUpsDisabled = false,
	isReadOnly = false,
}: BizyDocumentRendererProps) {
	const { track } = useAnalytics();
	let cardCount = 0;

	// Evidence-node drill-in: clicking a chart bar / heatmap cell / table row
	// sends a renderer-built scoped follow-up (the agent doesn't author these).
	// Disabled in read-only views and while a previous turn is processing.
	const drillEnabled = !isReadOnly && !!onDrill && !followUpsDisabled;
	const drill = (nodeType: 'chart' | 'matrix' | 'table' | 'scorecard', label: string, opts?: { title?: string; variant?: string }) => {
		const entity = label.trim();
		if (!onDrill || !entity) return;
		track(AnalyticsEvent.Bizy.EvidenceDrilled, { nodeType, variant: opts?.variant, path: window.location.pathname });
		// The node title carries the metric + period, so prefixing it makes the
		// follow-up a standalone question (the agent need not resolve it from
		// history) and read sensibly in the transcript.
		const verb = nodeType === 'matrix' ? `break down ${entity}` : `tell me more about ${entity}`;
		const title = opts?.title?.trim();
		const message = title ? `In "${title}", ${verb}.` : `${verb.charAt(0).toUpperCase()}${verb.slice(1)}.`;
		onDrill(message);
	};

	// Expand a truncated evidence node ("Top N of M" → see the rest): re-ask for
	// the full set. The agent's cardinality default re-renders it as a table (the
	// representation that can show every row) — so we don't show all M bars here.
	const expand = (nodeType: 'chart' | 'matrix' | 'table', count: number, opts?: { title?: string; noun?: string }) => {
		if (!onDrill) return;
		track(AnalyticsEvent.Bizy.EvidenceDrilled, { nodeType, action: 'expand_groups', path: window.location.pathname });
		const noun = pluralize(opts?.noun) || (nodeType === 'matrix' ? 'rows' : 'groups');
		const title = opts?.title?.trim();
		const message = title ? `In "${title}", show all ${count} ${noun}.` : `Show all ${count} ${noun}.`;
		onDrill(message);
	};
	// Index of the last markdown node — only this one receives streaming UI
	// (tool progress / thinking phrase / pulsing cursor) when the turn is
	// in flight. Earlier markdown nodes were closed by an intervening card
	// and are finalized segments even mid-stream.
	const lastMarkdownIdx = (() => {
		for (let i = nodes.length - 1; i >= 0; i -= 1) {
			if (nodes[i].type === 'markdown') return i;
		}
		return -1;
	})();

	return (
		<Box data-testid="bizy-document-renderer">
			{nodes.map((node, idx) => {
				if (node.type === 'markdown') {
					const isActiveMarkdown = idx === lastMarkdownIdx;
					const isLastNode = idx === nodes.length - 1;
					const isWorkingPlan = node.payload.role === 'working_plan';
					// Inactive markdown nodes are closed segments — render as
					// complete even when the document as a whole is still
					// streaming, so they don't show their own "Thinking..."
					// label or pulsing cursor.
					return (
						<Box key={`md-${idx}`} mb={isLastNode ? 0 : 4}>
							<BizyStreamingMessage
								content={node.payload.content}
								fontSize={fontSize}
								isComplete={isComplete || !isActiveMarkdown}
								onContentUpdate={onContentUpdate}
								toolProgress={isActiveMarkdown ? toolProgress : undefined}
								thinkingPhrase={isActiveMarkdown ? thinkingPhrase : undefined}
								progressPlacement={isWorkingPlan ? 'below' : 'above'}
								onSuggestedPageClick={onSuggestedPageClick}
							/>
						</Box>
					);
				}

				if (
					node.type === 'recognition_draft_card' ||
					node.type === 'comment_draft_card' ||
					node.type === 'recognition_view_card' ||
					node.type === 'recognition_appreciation_card' ||
					node.type === 'user_admin_batch_confirm_card'
				) {
					return <UiBlockRenderer key={`card-${idx}`} block={node as BizyUiBlock} fontSize={fontSize} isReadOnly={isReadOnly} />;
				}

				if (node.type === 'scorecard') {
					// Headline KPIs that lead a rich answer. Read-only views never
					// offer the drill affordance (no sending on the viewer's behalf).
					return (
						<BizyScorecard
							key={`scorecard-${idx}`}
							title={node.payload.title}
							metrics={node.payload.metrics}
							onSelectMetric={drillEnabled ? (metric) => drill('scorecard', metric.label, { title: node.payload.title }) : undefined}
							isDisabled={followUpsDisabled}
						/>
					);
				}

				if (node.type === 'chart') {
					// Evidence chart (bar/line/stacked/diverging). Drillable by category.
					return (
						<BizyChart
							key={`chart-${idx}`}
							variant={node.payload.variant}
							title={node.payload.title}
							xLabel={node.payload.xLabel}
							yLabel={node.payload.yLabel}
							yFormat={node.payload.yFormat}
							series={node.payload.series}
							referenceValue={node.payload.referenceValue}
							referenceLabel={node.payload.referenceLabel}
							totalGroups={node.payload.totalGroups}
							selection={node.payload.selection}
							onExpand={
								drillEnabled && node.payload.totalGroups != null
									? () => expand('chart', node.payload.totalGroups as number, { title: node.payload.title, noun: node.payload.xLabel })
									: undefined
							}
							onDrill={
								drillEnabled
									? (category, seriesName) => {
											// On a multi-series chart, name the clicked segment too.
											const entity = node.payload.series.length > 1 && seriesName ? `${seriesName} for ${category}` : category;
											drill('chart', entity, { title: node.payload.title, variant: node.payload.variant });
										}
									: undefined
							}
						/>
					);
				}

				if (node.type === 'matrix') {
					// 2-D categorical grid (heatmap) evidence. Drillable by cell.
					return (
						<BizyMatrix
							key={`matrix-${idx}`}
							title={node.payload.title}
							rowLabel={node.payload.rowLabel}
							columnLabel={node.payload.columnLabel}
							valueFormat={node.payload.valueFormat}
							zeroFill={node.payload.zeroFill}
							rows={node.payload.rows}
							columns={node.payload.columns}
							cells={node.payload.cells}
							totalRows={node.payload.totalRows}
							selection={node.payload.selection}
							onExpand={
								drillEnabled && node.payload.totalRows != null
									? () => expand('matrix', node.payload.totalRows as number, { title: node.payload.title, noun: node.payload.rowLabel })
									: undefined
							}
							onDrill={drillEnabled ? (label) => drill('matrix', label, { title: node.payload.title }) : undefined}
						/>
					);
				}

				if (node.type === 'table') {
					// Structured table evidence (sortable, CSV/copy). Drillable by row.
					return (
						<BizyTable
							key={`table-${idx}`}
							title={node.payload.title}
							columns={node.payload.columns}
							rows={node.payload.rows}
							totalRows={node.payload.totalRows}
							selection={node.payload.selection}
							defaultSortColumn={node.payload.defaultSortColumn}
							onExpand={
								drillEnabled && node.payload.totalRows != null
									? () => expand('table', node.payload.totalRows as number, { title: node.payload.title, noun: node.payload.columns[0]?.label })
									: undefined
							}
							onDrill={drillEnabled ? (label) => drill('table', label, { title: node.payload.title }) : undefined}
						/>
					);
				}

				if (node.type === 'source_details') {
					// The "How I got this" provenance disclosure trailing a data answer.
					return <BizySourceDetails key={`source-details-${idx}`} sourceDetails={node.payload.sourceDetails} caveats={node.payload.caveats} />;
				}

				if (node.type === 'follow_ups') {
					// The agent emits follow-ups as their own node, ordered after the
					// answer/takeaways and before the sources node — so rendering it
					// in place gives the correct layout. Read-only views (shared/admin)
					// never offer to send on the viewer's behalf.
					if (isReadOnly || !onSelectFollowUp) return null;
					return (
						<BizyFollowUpSuggestions
							key={`followups-${idx}`}
							suggestions={node.payload.suggestions}
							onSelect={onSelectFollowUp}
							isDisabled={followUpsDisabled}
							fontSize={fontSize}
						/>
					);
				}

				// Legacy card node — wrap as a single-element CardNode array for
				// the per-card dispatcher. cardIndexOffset gives the card a
				// stable index across the document so onChoiceUpdate
				// (messageKey, cardIndex, choice) targets the right card.
				const offset = cardCount;
				cardCount += 1;
				const card: CardNode = {
					type: node.type,
					payload: node.payload,
					choice: nodeChoices?.[offset],
				};
				return (
					<BizyCardRenderer
						key={`card-${idx}`}
						cards={[card]}
						messageKey={messageKey}
						cardIndexOffset={offset}
						onConfirm={onConfirm}
						onChoiceUpdate={onChoiceUpdate}
						isReadOnly={isReadOnly}
					/>
				);
			})}
		</Box>
	);
}
